import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { Search, X } from "lucide-react"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { GRAPH_FARBEN, KindIcon } from "@/components/search/RelationGraph"

/**
 * Globale Suche in der Kopfzeile — dasselbe Feld wie auf /projekte, nur klein.
 *
 * Bis 12.08.2026 feuerte sie bei jedem Tastendruck DREI Abfragen live gegen
 * HERO (Kontakte, Projekte, Dokumente). Das war traege, fand keine Rufnummern,
 * keine Mails und keine Anrufe — und HEROs Volltextsuche hinkt ohnehin Minuten
 * hinterher. Jetzt: ein Aufruf gegen `/api/v1/search`, komplett aus Postgres.
 *
 * Klick fuehrt in die Vollansicht (`/projekte?contact_id=…`), nicht auf eine
 * Detailseite. Der Zweck ist ja gerade, den ganzen Zusammenhang zu sehen.
 *
 * Tastatur: Cmd/Ctrl+K fokussiert, Esc schliesst.
 */

type Treffer = {
  kind: string
  id: number
  title: string
  subtitle?: string
  phone?: string
  status?: string
  contact_id?: number | null
  project_id?: number | null
  tenant_slug?: string | null
}

type SuchAntwort = {
  groups: Record<string, Treffer[]>
  total: number
  phone: string | null
  degraded?: boolean
}

const GRUPPEN: Array<{ id: string; label: string; kind: string }> = [
  { id: "contacts", label: "Personen & Firmen", kind: "contact" },
  { id: "projects", label: "Vorgänge", kind: "project" },
  { id: "documents", label: "Belege", kind: "document" },
  { id: "emails", label: "E-Mails", kind: "email" },
  { id: "calls", label: "Anrufe", kind: "call" },
  { id: "appointments", label: "Termine", kind: "appointment" },
  { id: "tasks", label: "Aufgaben", kind: "task" },
]

export function GlobalSearch() {
  const [tenantSlug] = useTenantFilter()
  const navigate = useNavigate()
  const [q, setQ] = useState("")
  const [debounced, setDebounced] = useState("")
  const [open, setOpen] = useState(false)
  // 🔑 Zugeklappt ist die Suche nur eine Lupe. Sie sass als 320-px-Kasten
  // dauerhaft ueber der Kopfzeile und verdeckte dort Inhalt — fuer einen
  // Griff, der selten kommt und ohnehin auf ⌘K hoert.
  const [ausgeklappt, setAusgeklappt] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 160)
    return () => clearTimeout(t)
  }, [q])

  const enabled = debounced.trim().length >= 2

  const suche = useQuery<SuchAntwort>({
    queryKey: ["global-suche", debounced, tenantSlug || ""],
    enabled,
    staleTime: 20_000,
    queryFn: async () => {
      const url = `/api/v1/search?q=${encodeURIComponent(debounced.trim())}&limit=4`
        + (tenantSlug ? `&tenant=${encodeURIComponent(tenantSlug)}` : "")
      const r = await fetch(url, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })

  // Cmd/Ctrl+K Shortcut
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if ((ev.metaKey || ev.ctrlKey) && ev.key === "k") {
        ev.preventDefault()
        setAusgeklappt(true)
        setOpen(true)
        // Erst nach dem Ausklappen gibt es ein Feld, das Fokus nehmen kann.
        requestAnimationFrame(() => inputRef.current?.focus())
      }
      if (ev.key === "Escape") {
        setOpen(false)
        inputRef.current?.blur()
        if (!q) setAusgeklappt(false)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [q])

  // Outside-Click schliesst Dropdown
  useEffect(() => {
    const onClick = (ev: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setOpen(false)
        // Mit Suchbegriff bleibt das Feld stehen — ein Filter, der noch wirkt,
        // darf nicht hinter einem Icon verschwinden.
        if (!q) setAusgeklappt(false)
      }
    }
    if (open) document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [open, q])

  const oeffne = (t: Treffer) => {
    const p = new URLSearchParams()
    if (t.kind === "contact") p.set("contact_id", String(t.id))
    else if (t.kind === "project") p.set("project_id", String(t.id))
    else if (t.contact_id) p.set("contact_id", String(t.contact_id))
    else if (t.project_id) p.set("project_id", String(t.project_id))
    else return
    p.set("q", debounced.trim())
    setOpen(false)
    setQ("")
    navigate(`/projekte?${p}`)
  }

  const alleAnzeigen = () => {
    setOpen(false)
    const wert = debounced.trim()
    setQ("")
    navigate(`/projekte?q=${encodeURIComponent(wert)}`)
  }

  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform)
  const gefunden = GRUPPEN
    .map((g) => ({ ...g, items: suche.data?.groups?.[g.id] || [] }))
    .filter((g) => g.items.length > 0)

  // Zugeklappt: nur die Lupe, in derselben Hoehe wie das Feld.
  if (!ausgeklappt && !q) {
    return (
      <div ref={containerRef} style={{ position: "fixed", top: "calc(12px + var(--stoerung-h, 0px))", right: 24, zIndex: 100 }}>
        <button
          type="button"
          onClick={() => {
            setAusgeklappt(true)
            requestAnimationFrame(() => inputRef.current?.focus())
          }}
          title={`Suchen (${isMac ? "⌘K" : "Strg+K"}): Name, Nummer, Beleg, Mail`}
          aria-label="Suchen"
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 36, height: 36, borderRadius: 8,
            background: "#fff", border: "1px solid #e5e7eb",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)", cursor: "pointer", color: "#64748b",
          }}
        >
          <Search className="w-4 h-4" />
        </button>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: "fixed", top: "calc(12px + var(--stoerung-h, 0px))", right: 24, zIndex: 100,
        width: 320, maxWidth: "calc(100vw - 48px)",
      }}
    >
      <div style={{
        position: "relative",
        background: "#fff", borderRadius: 8,
        border: open ? "1px solid #1d4ed8" : "1px solid #e5e7eb",
        boxShadow: open ? "0 4px 12px rgba(0,0,0,0.08)" : "0 1px 3px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.15s, border-color 0.15s",
      }}>
        <Search className="w-4 h-4" style={{
          position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
          color: "#94a3b8", pointerEvents: "none",
        }} />
        <input
          ref={inputRef}
          type="text"
          placeholder="Suchen…  Name, Nummer, Beleg, Mail"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Enter" && enabled) alleAnzeigen() }}
          style={{
            width: "100%", padding: "8px 60px 8px 36px",
            border: "none", outline: "none", borderRadius: 8, fontSize: 13,
            background: "transparent",
          }}
        />
        {q ? (
          <button
            onClick={() => { setQ(""); setOpen(false); setAusgeklappt(false) }}
            style={{
              position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
              background: "transparent", border: "none", padding: 4, cursor: "pointer",
              color: "#94a3b8",
            }}
            aria-label="Suche leeren"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <kbd style={{
            position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
            fontSize: 12, color: "#94a3b8",
            padding: "2px 6px", border: "1px solid #e5e7eb", borderRadius: 4,
            background: "#f8fafc", fontFamily: "system-ui",
          }}>{isMac ? "⌘K" : "Ctrl+K"}</kbd>
        )}
      </div>

      {open && enabled && (
        <div style={{
          marginTop: 6, background: "#fff", borderRadius: 8,
          border: "1px solid #e5e7eb", boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          maxHeight: "calc(100vh - 120px)", overflowY: "auto",
        }}>
          {suche.data?.degraded && (
            <div style={{
              padding: "7px 14px", fontSize: 12, color: "#92400e", background: "#fffbeb",
              borderBottom: "1px solid #fde68a",
            }}>
              Teilergebnis — nicht alle Bereiche durchsucht.
            </div>
          )}

          {gefunden.length === 0 && !suche.isFetching && (
            <div style={{ padding: 16, fontSize: 13, color: "#94a3b8", textAlign: "center" }}>
              Nichts gefunden für „{debounced}"
            </div>
          )}

          {gefunden.map((g) => (
            <div key={g.id}>
              <div style={{
                padding: "8px 14px 4px", display: "flex", alignItems: "center", gap: 6,
                fontSize: 12, fontWeight: 700,
                color: GRAPH_FARBEN[g.kind as never] || "#94a3b8",
                textTransform: "uppercase", letterSpacing: 0.5,
                borderTop: "1px solid #f8fafc",
              }}>
                <KindIcon kind={g.kind as never} size={11} />
                {g.label}
                <span style={{ fontWeight: 400, color: "#cbd5e1" }}>({g.items.length})</span>
              </div>
              {g.items.map((t) => (
                <ResultRow
                  key={`${t.kind}-${t.id}`}
                  title={t.title}
                  sub={[t.subtitle, t.phone, t.status].filter(Boolean).join(" · ")}
                  onClick={() => oeffne(t)}
                />
              ))}
            </div>
          ))}

          {!!suche.data?.total && (
            <button
              onClick={alleAnzeigen}
              style={{
                display: "block", width: "100%", textAlign: "center", padding: "9px 14px",
                background: "#f8fafc", border: "none", borderTop: "1px solid #f1f5f9",
                cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#334155",
              }}
            >
              Alle {suche.data.total} Treffer in der Suche öffnen
            </button>
          )}

          {suche.isFetching && (
            <div style={{ padding: 12, fontSize: 12, color: "#94a3b8", textAlign: "center", borderTop: "1px solid #f1f5f9" }}>
              Sucht…
            </div>
          )}
        </div>
      )}

      {open && !enabled && q.length > 0 && (
        <div style={{
          marginTop: 6, padding: 12, background: "#fff", borderRadius: 8,
          border: "1px solid #e5e7eb", fontSize: 12, color: "#94a3b8", textAlign: "center",
        }}>
          Mindestens 2 Zeichen tippen…
        </div>
      )}
    </div>
  )
}

function ResultRow({ title, sub, onClick }: {
  title: string; sub?: string; onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "block", width: "100%", textAlign: "left",
        padding: "8px 14px", background: "transparent", border: "none",
        cursor: "pointer", fontSize: 13, color: "#1a1a2e",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <div style={{
        fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {title}
      </div>
      {sub && (
        <div style={{
          fontSize: 12, color: "#64748b", marginTop: 2,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {sub}
        </div>
      )}
    </button>
  )
}
